<template>
    <div class="table-detail">
        <TableToolbar
            :fields="tableFields"
            :filters="filterConditions"
            :selectedRecords="selectedRecords"
            :table-id="tableId"
            :table="table"
            :hiddenFields="hiddenFields"
            :lockedFields="lockedFields"
            :sort="currentSort"
            @filter-updated="onFilterUpdated"
            @search="onSearch"
            @records-deleted="onRecordsDeleted"
            @records-duplicated="onRecordsDuplicated"
            @edit-records="onEditRecords"
            @show-hide-fields="openHideFieldsPanel"
            @clear-hidden-fields="showAllFields"
            @view-created="onViewCreated"
            @show-view="onShowView"
            @edit-view="$_.noop"
            @create-new-view="$_.noop"
             @open-import-modal="openImportModal"
            :current-view="currentView"
            v-if="table && !loading"
        />

        <div class="table-body">
            <TableHeaders
                v-if="table && !loading"
                :table="table"
                :isSelectionModeActive="isSelectionModeActive"
                :selectedRecords="selectedRecords"
                :isAllSelected="isAllSelected"
                @refresh-table="fetchTable"
                @toggle-select-all="toggleSelectAll"
                :visibleFields="visibleFields"
                @hide-field="hideField"
            />
            <TableRecords
                v-if="table && !loading"
                :table="table"
                :records="filteredRecords"
                :isSelectionModeActive="isSelectionModeActive"
                :selectedRecords="selectedRecords"
                @record-updated="recordUpdated"
                @toggle-selection="toggleSelection"
                :visibleFields="visibleFields"
            />
            <div v-if="loading" class="loading-overlay">
                <a-spin size="large" />
            </div>
        </div>

        <TableFooter
            :record-count="total"
            :table-id="tableId"
            :disabled="loading || isCreatingRecord"
            @create-new-record="onCreateNewRecord"
            @open-import-modal="openImportModal"
        />

        <Modal2
            v-if="showEditMultiRecordModal"
            @cancel="closeEditMultiRecordModal"
            :showFooter="false"
            title="Edit Multiple Records"
        >
            <EditMultiRecord
                :table="table"
                :fields="tableFields"
                :selectedRecords="selectedRecords"
                @records-updated="onRecordsUpdated"
                @cancel="closeEditMultiRecordModal"
            />
        </Modal2>

        <!-- HideFields Bileşeni -->
        <HideFields
            v-if="showHideFieldsPanel"
            :fields="tableFields"
            :hiddenFields="hiddenFields"
            @close="closeHideFieldsPanel"
            @hide-field="hideField"
            @show-field="showField"
            @show-all-fields="showAllFields"
            @hide-all-fields="hideAllFields"
        />

        <Modal2
            v-if="showImportModal"
            title="Kayıtları içeri Aktar"
            @cancel="closeImportModal"
            class="import-records-modal"
            :show-footer="false"
        >
            <ImportRecords
                :table="table"
                :fields="tableFields"
                @import-records="handleImport"
                @cancel="closeImportModal"
                :on-completed="closeImportModal"
                :on-cancelled="closeImportModal"
            />
        </Modal2>
    </div>
</template>
<script>
import Vue from "vue";
import TableToolbar from "./TableToolbar.vue";
import TableHeaders from "./TableHeaders.vue";
import TableRecords from "./TableRecords.vue";
import TableFooter from "./TableFooter.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import EditMultiRecord from "./components/EditMultiRecord.vue";
import { TABLE_API } from "../../../../api/table";
import HideFields from "./components/HideFields.vue";
import ImportRecords from "./components/ImportRecords.vue";

export default Vue.extend({
    name: "TableDetail",
    components: {
        TableFooter,
        TableRecords,
        TableHeaders,
        TableToolbar,
        Modal2,
        EditMultiRecord,
        HideFields,
        ImportRecords,
    },
    props: {
        defaultTableId: {
            type: String,
        },
    },
    data() {
        return {
            table: null,
            records: [],
            total: 0,
            loading: true,
            isCreatingRecord: false,
            selectedRecords: [],
            filterConditions: [], // Filtre koşullarını burada tutuyoruz
            searchQuery: "",
            showEditMultiRecordModal: false,
            hiddenFields: [],
            showHideFieldsPanel: false,
            lockedFields: [],
            currentSort: {},
            currentView: null,
            showImportModal: false,
        };
    },
    computed: {
        tableId() {
            return this.defaultTableId || this.$route.params.id;
        },
        isAllSelected() {
            return this.selectedRecords.length === this.records.length;
        },
        isSelectionModeActive() {
            return this.selectedRecords.length > 0;
        },
        tableFields() {
            return this.table ? this.table.fields : [];
        },
        filteredRecords() {
            if (!this.searchQuery) {
                return this.records;
            }
            const lowercaseQuery = this.searchQuery.toLowerCase();
            return this.records.filter((record) => {
                return Object.values(record.data).some((value) => {
                    if (Array.isArray(value)) {
                        return value.some((item) => String(item).toLowerCase().includes(lowercaseQuery));
                    }
                    return String(value).toLowerCase().includes(lowercaseQuery);
                });
            });
        },
        visibleFields() {
            return this.tableFields.filter((field) => !this.hiddenFields.includes(field._id));
        },
    },
    methods: {
        async fetchTable() {
            this.loading = true;
            try {
                const table = await TABLE_API.getTableById(this.tableId);
                this.table = table;
                const records = await TABLE_API.listRecords(this.tableId, this.filterConditions);
                this.records = records.items;
                this.total = records.total;
            } catch (error) {
                console.error(this.$t("tables.errorPullTable"), error);
                this.$message.error(this.$t("tables.errorPullTable"));
            } finally {
                this.loading = false;
            }
        },
        async onCreateNewRecord() {
            this.isCreatingRecord = true;
            try {
                const newRecord = await TABLE_API.createEmptyRecord(this.tableId);
                this.records.push(newRecord);
                // Başarılı mesajını göster
                this.$message.success(this.$t("tables.newRecordSucs"));
            } catch (error) {
                console.error(this.$t("tables.failedNewRecord"), error);
                // Hata mesajını göster
                this.$message.error(this.$t("tables.newRecordCreateError"));
            }
            this.isCreatingRecord = false;
        },
        onApplyFilter(filterConditions) {
            this.filterConditions = filterConditions;
            this.fetchTable();
        },
        recordUpdated(record) {
            const index = this.records.findIndex((r) => r._id === record._id);
            if (index !== -1) {
                this.records.splice(index, 1, record);
            }
        },
        toggleSelectAll(isSelected) {
            if (isSelected) {
                this.selectedRecords = this.records.map((record) => record._id);
            } else {
                this.selectedRecords = [];
            }
        },
        toggleSelection(recordId) {
            const index = this.selectedRecords.indexOf(recordId);
            if (index > -1) {
                this.selectedRecords.splice(index, 1);
            } else {
                this.selectedRecords.push(recordId);
            }
        },
        handleKeyDown(event) {
            if ((event.ctrlKey || event.metaKey) && event.key === "a") {
                event.preventDefault();
                this.toggleSelectAll(!this.isAllSelected);
            } else if ((event.metaKey || event.ctrlKey) && event.key === "r") {
                event.preventDefault();
                this.fetchTable();
            }
        },
        onFilterUpdated(filters) {
            this.filterConditions = filters;
            this.fetchTable();
        },
        onSearch(query) {
            this.searchQuery = query;
        },
        onRecordsDeleted(deletedRecordIds) {
            this.records = this.records.filter((record) => !deletedRecordIds.includes(record._id));
            this.selectedRecords = [];
            this.total = this.records.length;
        },
        async onRecordsDuplicated(duplicatedRecordIds) {
            this.selectedRecords = [];
            await this.fetchTable();
            this.scrollToLastRecord();
        },
        scrollToLastRecord() {
            const lastRecord = this.records[this.records.length - 1];
            if (lastRecord) {
                const lastRecordElement = document.querySelector(`[data-record-id="${lastRecord._id}"]`);
                if (lastRecordElement) {
                    lastRecordElement.scrollIntoView({ behavior: "smooth" });
                }
            }
        },
        onEditRecords() {
            this.showEditMultiRecordModal = true;
        },
        closeEditMultiRecordModal() {
            this.showEditMultiRecordModal = false;
        },
        async onRecordsUpdated() {
            await this.fetchTable();
            this.selectedRecords = [];
            this.closeEditMultiRecordModal();
        },
        openHideFieldsPanel() {
            this.showHideFieldsPanel = true;
        },
        closeHideFieldsPanel() {
            this.showHideFieldsPanel = false;
        },
        hideField(fieldId) {
            if (!this.hiddenFields.includes(fieldId)) {
                this.hiddenFields.push(fieldId);
            }
        },
        showField(fieldId) {
            this.hiddenFields = this.hiddenFields.filter((id) => id !== fieldId);
        },
        showAllFields() {
            this.hiddenFields = [];
        },
        hideAllFields() {
            this.hiddenFields = this.tableFields.map((field) => field._id);
        },
        onViewCreated(newView) {
            if (this.table && this.table.views) {
                this.table.views.push(newView);
            } else if (this.table) {
                this.$set(this.table, "views", [newView]);
            }
        },
        onShowView(view) {
            if (view) {
                this.currentView = view;
                this.filterConditions = view.filters || [];
                this.currentSort = view.sort || {};
                this.hiddenFields = view.hiddenFields || [];
                this.lockedFields = view.lockedFields || [];
                this.searchQuery = view.search || "";
            } else {
                // Main Table seçildiğinde varsayılan değerlere dön
                this.currentView = null;
                this.filterConditions = [];
                this.currentSort = {};
                this.hiddenFields = [];
                this.lockedFields = [];
                this.searchQuery = "";
            }
            this.fetchTable();
        },
        openImportModal() {
            this.showImportModal = true;
        },
        closeImportModal() {
            this.showImportModal = false;
        },
        async handleImport(values) {
            try {
                // İçeri aktarımın başladığını belirten mesajı göster
                this.$message.info(
                    "İçeri aktarım başladı. Verilerinizin büyüklüğüne göre birkaç dakika sürebilir. Lütfen bekleyin..."
                );

                // İçeri aktarım işlemini başlat
                await TABLE_API.createRecordMulti(this.tableId, values);
            } catch (error) {
                console.error("İçeri aktarım hatası:", error);
                // Hata mesajını göster
                this.$message.error("İçeri aktarım sırasında bir hata oluştu.");
            } finally {
                // İçeri aktarım penceresini kapat
                this.closeImportModal();
            }
        },
    },
    mounted() {
        this.fetchTable();
        document.addEventListener("keydown", this.handleKeyDown);
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleKeyDown);
    },
});
</script>
<style scoped lang="scss">
.table-body {
    height: calc(100vh - 155px);
    max-height: calc(100vh - 155px);
    overflow-y: auto;
    width: 100%;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 1000;
}
</style>
<style lang="scss">
.import-records-modal {
    .ant-modal {
        width: 1300px !important;
    }
}
</style>
